import { MAX_MEALS_PER_SLOT, type PlannedMeal, type MealType } from '../../redux/mealPlanSlice';
import { entryTitle, isEntryMeal, isExclusiveEntryMeal, type EntryTranslate } from '../../redux/mealEntry';

/** Which shape the plan sheet opens in. */
export type PlanSheetMode = 'add' | 'move';

/**
 * What tapping a slot in the plan sheet should do. Kept out of the component so
 * the branch that decides "move it" vs "add a duplicate" vs "ask first" can be
 * tested directly — it is where #564's sibling bug lived.
 */
export type SlotTapOutcome =
  | { kind: 'noop' }
  | { kind: 'move'; mealId: string }
  | { kind: 'add' }
  | { kind: 'confirmSlotOccupied'; firstMealId: string; options: SlotActionOption[] }
  | { kind: 'confirmSameRecipeElsewhere' };

/**
 * What the user can do with a slot that already holds something.
 *
 * - `swap`    — the two slots trade contents. Only offered when the dish being
 *               moved is alone in its slot: a whole-slot swap would otherwise
 *               drag its slot-mates along, which is not what "move this dish"
 *               means.
 * - `replace` — evict what is there and put this dish in its place.
 * - `stack`   — both dishes share the slot (a main plus a side).
 */
export type SlotAction = 'swap' | 'replace' | 'stack';

/** Why an action is offered but not available, for the greyed-out label. */
export type SlotActionBlocker = 'slotFull' | 'entryOwnsSlot';

export interface SlotActionOption {
  action: SlotAction;
  enabled: boolean;
  blockedBy?: SlotActionBlocker;
}

/**
 * The choices a tap on an occupied slot offers.
 *
 * Deliberately never decides for the user (#563/#564 follow-up): the sheet used to
 * resolve a move-mode tap straight to "join that slot", so replacing the dish
 * already there — or trading places with it — was unreachable. Unavailable
 * actions stay in the list, disabled with a reason, because a row that simply
 * does nothing when tapped reads as broken.
 */
export function slotActionOptions({
  movingMeal,
  sourceSlotMeals = [],
  slotMeals,
}: {
  /** The dish being moved. Absent in add mode — there is nothing to swap with. */
  movingMeal?: PlannedMeal;
  /** Everything in the slot the dish is leaving, including the dish itself. */
  sourceSlotMeals?: PlannedMeal[];
  /** Everything in the slot being tapped. */
  slotMeals: PlannedMeal[];
}): SlotActionOption[] {
  const options: SlotActionOption[] = [];

  // A swap needs a source slot, and only means "these two trade places" when
  // the dish travels alone.
  if (movingMeal && sourceSlotMeals.length <= 1) {
    options.push({ action: 'swap', enabled: true });
  }

  options.push({ action: 'replace', enabled: true });

  const holdsEntry = slotMeals.some(isExclusiveEntryMeal);
  const others = movingMeal ? slotMeals.filter(m => m.id !== movingMeal.id) : slotMeals;
  if (holdsEntry) {
    // resto and "rien" own their slot alone. A note or leftovers are the main
    // and take a side like any dinner.
    options.push({ action: 'stack', enabled: false, blockedBy: 'entryOwnsSlot' });
  } else if (others.length >= MAX_MEALS_PER_SLOT) {
    options.push({ action: 'stack', enabled: false, blockedBy: 'slotFull' });
  } else {
    options.push({ action: 'stack', enabled: true });
  }

  return options;
}

/**
 * The occurrences of a recipe that are still ahead of the user — the ones it
 * makes sense to offer to move. Sorted nearest-first so `[0]` is "the next time
 * you're making this".
 *
 * Deliberately spans every week in the plan. Scoping this to the week on screen
 * meant an occurrence in another week went unnoticed, and the sheet silently
 * created a duplicate where the user expected a reschedule.
 */
export function upcomingEntriesForRecipe(
  allEntriesForRecipe: PlannedMeal[],
  todayISO: string,
): PlannedMeal[] {
  return allEntriesForRecipe
    .filter(m => m.status !== 'cooked' && !!m.scheduledFor && m.scheduledFor >= todayISO)
    .slice()
    .sort((a, b) => a.scheduledFor.localeCompare(b.scheduledFor));
}

export interface SlotTapInput {
  mode: PlanSheetMode;
  /** The meal being moved. Only meaningful in `'move'` mode. */
  movingMeal?: PlannedMeal;
  /** Everything in the slot the moved dish is leaving. Only used for `'move'`. */
  sourceSlotMeals?: PlannedMeal[];
  /** Set once the user has explicitly asked for a second helping. */
  allowDuplicate?: boolean;
  recipeId?: string;
  date: string;
  mealType: MealType;
  /** Meals already sitting in the tapped slot. */
  slotMeals: PlannedMeal[];
  /** Result of `upcomingEntriesForRecipe`. */
  upcomingEntries: PlannedMeal[];
}

export function resolveSlotTap({
  mode,
  movingMeal,
  sourceSlotMeals,
  allowDuplicate = false,
  recipeId,
  date,
  mealType,
  slotMeals,
  upcomingEntries,
}: SlotTapInput): SlotTapOutcome {
  // Move mode. An empty destination is unambiguous, so it moves on the tap and
  // the toast's undo is the safety net. An occupied one is not: joining it,
  // replacing what is there and trading places are three different intentions,
  // so the user picks.
  if (mode === 'move' && movingMeal) {
    if (movingMeal.scheduledFor === date && movingMeal.mealType === mealType) {
      return { kind: 'noop' };
    }
    const occupants = slotMeals.filter(m => m.id !== movingMeal.id);
    if (occupants.length === 0) return { kind: 'move', mealId: movingMeal.id };
    return {
      kind: 'confirmSlotOccupied',
      firstMealId: occupants[0].id,
      options: slotActionOptions({ movingMeal, sourceSlotMeals, slotMeals: occupants }),
    };
  }

  // This exact slot already holds the recipe — the badge already says so.
  const alreadyHere = recipeId
    ? slotMeals.some(m => String(m.recipeId) === String(recipeId))
    : false;
  if (alreadyHere) return { kind: 'noop' };

  // Slot taken by something else: replace it, or stack alongside it.
  if (slotMeals.length > 0) {
    return {
      kind: 'confirmSlotOccupied',
      firstMealId: slotMeals[0].id,
      options: slotActionOptions({ slotMeals }),
    };
  }

  // Empty slot, but the recipe is planned elsewhere — offer to move rather than
  // quietly planning it twice, unless the user asked for exactly that.
  const elsewhere = upcomingEntries.filter(
    m => !(m.scheduledFor === date && m.mealType === mealType),
  );
  if (elsewhere.length > 0 && !allowDuplicate) {
    return { kind: 'confirmSameRecipeElsewhere' };
  }

  return { kind: 'add' };
}

/**
 * Whether a slot tap has to ask « Vous avez cuisiné cette recette ce jour-là ? »
 * first (#846). The app never assumes a meal on a day that is over was cooked:
 * a tap one day too high once said a dinner had been cooked that nobody had
 * made. The answer travels as `options.cooked` to `addToWeek`. A move in move
 * mode keeps the meal's own status, so it never asks.
 */
export function asksIfCooked({
  isMoving,
  date,
  today,
}: {
  isMoving: boolean;
  date: string;
  today: string;
}): boolean {
  return !isMoving && date < today;
}

/**
 * What to call a dish sitting in a slot. A slot can hold a resto, leftovers, a
 * note or « rien ce jour-là » as well as a recipe; those carry no
 * `recipe.title`, and an unnamed row made the plan look corrupted.
 */
export function slotDishLabel(
  meal: PlannedMeal,
  t: EntryTranslate,
  resolveTitle: (recipeId: string) => string | undefined = () => undefined,
): string {
  if (isEntryMeal(meal)) return entryTitle(meal.entry!, resolveTitle, t);
  return meal.recipe?.title || meal.freeText || String(t('thisWeek.unknownRecipe'));
}

/**
 * « Croquettes de poisson et Salade de concombre » — the dishes of a slot as
 * one phrase, in the reader's language. The plan sheet names them before a
 * replace, and the toast names them after: « Remplacer le premier » never said
 * which one, and a cook who replaced a dinner kept its side without knowing (#845).
 */
export function formatDishList(labels: string[], language: string): string {
  if (labels.length <= 1) return labels[0] ?? '';
  try {
    return new Intl.ListFormat(language, { style: 'long', type: 'conjunction' }).format(labels);
  } catch {
    // unreported: an engine without Intl.ListFormat still gets every name.
    return labels.join(', ');
  }
}

/**
 * What a slot's row says. A tap moves the meal at once, and a plus reads as
 * « add », so a cook could not tell that it would take the dish off its day
 * (#863): every slot a move can reach says so in words. A day that is over names
 * its meal during a move; « ajouter un repas » is for the add sheet.
 */
export function slotRowCopy({
  isMoving,
  isPast,
  blocked,
}: {
  isMoving: boolean;
  isPast: boolean;
  blocked: boolean;
}): { action: 'moveHere' | 'add' | 'none'; emptyLabel: 'mealType' | 'logPast' } {
  const action = blocked ? 'none' : isMoving ? 'moveHere' : 'add';
  return { action, emptyLabel: isPast && !isMoving ? 'logPast' : 'mealType' };
}
